import axios from "axios";

export type TableSize = { name: string; bytes: number };

export type StorageSnapshot = {
    measuredAt: Date;
    attachmentBytes: number;
    attachmentCount: number;
    thumbnailBytes: number;
    thumbnailCount: number;
    otherBytes: number;
    otherCount: number;
    databaseBytes: number;
    largestTables: TableSize[];
    storageError: string | null;
};

export type DashboardDay = { day: string; created: number; resolved: number };

export type RecurringJob = {
    id: string;
    lastExecution: Date | null;
    lastJobState: string | null;
    nextExecution: Date | null;
    error: string | null;
};

export type DashboardJobs = {
    failed: number;
    enqueued: number;
    processing: number;
    recurring: RecurringJob[];
};

export type AdminDashboard = {
    storage: StorageSnapshot | null;
    people: { total: number; active: number; admins: number; disabled: number };
    projects: { total: number; organizations: number; portals: number };
    tickets: { total: number; open: number; overdue: number };
    activity: {
        created: number;
        resolved: number;
        comments: number;
        attachments: number;
        hoursLogged: number;
        peopleActive: number;
        portalRequests: number;
        days: DashboardDay[];
    };
    jobs: DashboardJobs | null;
};

type RecurringJobResponse = Omit<RecurringJob, "lastExecution" | "nextExecution"> & {
    lastExecution: string | null;
    nextExecution: string | null;
};

type AdminDashboardResponse = Omit<AdminDashboard, "storage" | "jobs"> & {
    storage: (Omit<StorageSnapshot, "measuredAt"> & { measuredAt: string }) | null;
    jobs: (Omit<DashboardJobs, "recurring"> & { recurring: RecurringJobResponse[] }) | null;
};

export type AdminDashboardApi = {
    get(): Promise<AdminDashboard>;
    sweepStorage(): Promise<void>;
};

export const adminDashboardApi: AdminDashboardApi = {
    async get() {
        const { data } = await axios.get<AdminDashboardResponse>("/api/v0/admin/dashboard");
        return {
            ...data,
            storage: data.storage
                ? { ...data.storage, measuredAt: new Date(data.storage.measuredAt) }
                : null,
            jobs: data.jobs
                ? {
                      ...data.jobs,
                      recurring: data.jobs.recurring.map((j) => ({
                          ...j,
                          lastExecution: j.lastExecution ? new Date(j.lastExecution) : null,
                          nextExecution: j.nextExecution ? new Date(j.nextExecution) : null,
                      })),
                  }
                : null,
        };
    },
    async sweepStorage() {
        await axios.post("/api/v0/admin/dashboard/storage-sweep");
    },
};
